import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, FileSpreadsheet, Image as ImageIcon, Film, Music,
  FileCode, FileArchive, File, PenLine, Link2, Star, Clock, Loader2, AlertTriangle,
} from 'lucide-react';
import TagChip from './TagChip';
import useAuthedBlob from './useAuthedBlob';
import { sopApi, formatRelative } from '../api';

// One icon per stored file_class, so the card communicates the source format
// even though everything opens in the same viewer.
const ICON_BY_CLASS = {
  pdf: FileText,
  office: FileText,
  image: ImageIcon,
  video: Film,
  audio: Music,
  text: FileCode,
  markdown: PenLine,
  csv: FileSpreadsheet,
  html: FileCode,
  archive: FileArchive,
  native: PenLine,
  link: Link2,
  other: File,
};

const LABEL_BY_CLASS = {
  pdf: 'PDF', office: 'Document', image: 'Image', video: 'Video', audio: 'Audio',
  text: 'Text', markdown: 'Markdown', csv: 'Spreadsheet', html: 'Web page',
  archive: 'Archive', native: 'Written in HQ', link: 'Link', other: 'File',
};

const ext = (doc) => (doc.originalExt || '').toUpperCase();

const SopCard = ({ doc, onOpen, view = 'grid' }) => {
  // Office files become PDFs whose first page is the deck/doc cover — that
  // makes a far better card than a generic icon, so fetch it when present.
  const { url: thumbUrl } = useAuthedBlob(doc.hasThumb ? sopApi.thumbUrl(doc.id) : null);

  const Icon = ICON_BY_CLASS[doc.docKind === 'native' ? 'native' : doc.fileClass] || File;
  const typeLabel = doc.docKind === 'native'
    ? LABEL_BY_CLASS.native
    : (ext(doc) || LABEL_BY_CLASS[doc.fileClass] || 'File');

  const processing = doc.renderStatus === 'pending' || doc.renderStatus === 'processing';
  const failed = doc.renderStatus === 'failed';

  const meta = (
    <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
      <span className="inline-flex items-center gap-1">
        <Icon className="w-3.5 h-3.5" />
        {typeLabel}
      </span>
      {doc.pageCount ? <span>{doc.pageCount} pages</span> : null}
      <span className="inline-flex items-center gap-1">
        <Clock className="w-3.5 h-3.5" />
        {formatRelative(doc.updatedAt || doc.createdAt)}
      </span>
      {doc.versionNo > 1 && <span>v{doc.versionNo}</span>}
      {processing && (
        <span className="inline-flex items-center gap-1 text-indigo-300">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Processing
        </span>
      )}
      {failed && (
        <span className="inline-flex items-center gap-1 text-amber-300">
          <AlertTriangle className="w-3.5 h-3.5" />
          Preview unavailable
        </span>
      )}
    </div>
  );

  const tags = (
    <div className="flex flex-wrap gap-1.5">
      {doc.tags.slice(0, view === 'grid' ? 3 : 6).map((t) => (
        <TagChip key={t.id} tag={t} size="xs" />
      ))}
      {doc.tags.length > (view === 'grid' ? 3 : 6) && (
        <span className="text-[11px] text-slate-500 self-center">
          +{doc.tags.length - (view === 'grid' ? 3 : 6)}
        </span>
      )}
    </div>
  );

  if (view === 'list') {
    return (
      <Link
        to={`/tiles/ksp_sops/view/${doc.id}`}
        onClick={() => onOpen && onOpen(doc)}
        className="flex items-start gap-4 p-4 bg-slate-800 border border-slate-700 rounded-lg
          hover:border-indigo-500/60 hover:bg-slate-800/80 transition group"
      >
        <div className="w-16 h-16 flex-shrink-0 rounded-md bg-slate-900 border border-slate-700 overflow-hidden flex items-center justify-center">
          {thumbUrl
            ? <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
            : <Icon className="w-6 h-6 text-slate-500" />}
        </div>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex items-start justify-between gap-3">
            <h3 className="font-semibold text-slate-100 group-hover:text-indigo-300 truncate">{doc.title}</h3>
            {doc.isFavorite && <Star className="w-4 h-4 text-amber-400 fill-amber-400 flex-shrink-0" />}
          </div>
          {doc.description && <p className="text-sm text-slate-400 line-clamp-2">{doc.description}</p>}
          {tags}
          {meta}
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/tiles/ksp_sops/view/${doc.id}`}
      onClick={() => onOpen && onOpen(doc)}
      className="flex flex-col bg-slate-800 border border-slate-700 rounded-lg overflow-hidden
        hover:border-indigo-500/60 hover:-translate-y-0.5 transition group"
    >
      <div className="h-32 bg-slate-900 border-b border-slate-700 flex items-center justify-center overflow-hidden">
        {thumbUrl
          ? <img src={thumbUrl} alt="" className="w-full h-full object-cover object-top" />
          : <Icon className="w-10 h-10 text-slate-600" />}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-slate-100 group-hover:text-indigo-300 line-clamp-2">{doc.title}</h3>
          {doc.isFavorite && <Star className="w-4 h-4 text-amber-400 fill-amber-400 flex-shrink-0" />}
        </div>
        {doc.description && <p className="text-sm text-slate-400 line-clamp-2 flex-1">{doc.description}</p>}
        {tags}
        {meta}
      </div>
    </Link>
  );
};

export default SopCard;
